import React from "react";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";

import {
  getKoraxModeColors,
  koraxTokens
} from "../../../designSystem";

const MOBILE_TOPBAR_HEIGHT = 56;
const MOBILE_TICKET_HEADER_HEIGHT = 58;
const MOBILE_FIXED_TOP =
  MOBILE_TOPBAR_HEIGHT + MOBILE_TICKET_HEADER_HEIGHT;

const useStyles = makeStyles(theme => {
  const colors = getKoraxModeColors(theme.mode);
  const dark = theme.mode === "dark";

  return {
    root: {
      flex: "0 0 auto",
      minHeight: 42,
      margin: "0 10px 8px",
      padding: "5px 8px",

      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,

      borderRadius: 11,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      boxShadow: koraxTokens.shadows.xs,

      [theme.breakpoints.down("sm")]: {
        position: "fixed",
        top: MOBILE_FIXED_TOP,
        left: 6,
        right: 6,

        width: "auto",
        minHeight: 42,
        margin: 0,
        padding: "5px 7px",

        zIndex: 890,
        borderRadius: 11,

        boxShadow: dark
          ? "0 6px 18px rgba(0,0,0,.28)"
          : "0 6px 18px rgba(15,23,42,.12)",

        transform: "translateZ(0)",
        WebkitTransform: "translateZ(0)"
      }
    },

    mobileSpacer: {
      display: "none",

      [theme.breakpoints.down("sm")]: {
        display: "block",
        flex: "0 0 auto",
        height: 50
      }
    },

    aiMode: {
      borderColor: dark
        ? "rgba(167,139,250,.28)"
        : "rgba(124,58,237,.18)",

      backgroundColor: dark
        ? "rgba(30,20,55,.98)"
        : "#FAF7FF"
    },

    humanMode: {
      borderColor: dark
        ? "rgba(56,189,248,.25)"
        : "rgba(21,94,239,.18)",

      backgroundColor: dark
        ? "rgba(15,32,51,.98)"
        : "#F8FAFF"
    },

    content: {
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 8
    },

    icon: {
      flexShrink: 0,
      width: 28,
      height: 28,

      display: "flex",
      alignItems: "center",
      justifyContent: "center",

      borderRadius: 9,
      fontSize: 15,
      fontWeight: 700
    },

    aiIcon: {
      color: dark ? "#C4B5FD" : "#7C3AED",
      backgroundColor: dark
        ? "rgba(124,58,237,.16)"
        : "rgba(124,58,237,.10)"
    },

    humanIcon: {
      color: dark ? "#7DD3FC" : "#155EEF",
      backgroundColor: dark
        ? "rgba(56,189,248,.14)"
        : "rgba(21,94,239,.09)"
    },

    textWrap: {
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 6
    },

    title: {
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",

      color: colors.textPrimary,
      fontSize: 12,
      fontWeight: 700,
      lineHeight: 1.2,

      [theme.breakpoints.down("sm")]: {
        fontSize: 11.5
      }
    },

    detail: {
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",

      color: colors.textMuted,
      fontSize: 10.5,
      lineHeight: 1.2,

      [theme.breakpoints.down("sm")]: {
        display: "none"
      }
    },

    action: {
      flexShrink: 0,
      minHeight: 30,
      padding: "0 11px",

      border: "none",
      borderRadius: koraxTokens.radius.pill,

      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,

      color: "#FFFFFF",
      fontFamily: koraxTokens.typography.fontFamily,
      fontSize: 10.5,
      fontWeight: 700,
      whiteSpace: "nowrap",
      cursor: "pointer",

      transition: koraxTokens.transitions.fast,

      "&:hover": {
        transform: "translateY(-1px)"
      },

      "&:active": {
        transform: "translateY(0)"
      },

      [theme.breakpoints.down("sm")]: {
        minHeight: 29,
        padding: "0 9px",
        fontSize: 10
      }
    },

    acceptAction: {
      background: "linear-gradient(135deg, #7C3AED, #6D28D9)",
      boxShadow: "0 6px 14px rgba(124,58,237,.18)"
    },

    resumeAction: {
      background: "linear-gradient(135deg, #155EEF, #0BA5EC)",
      boxShadow: "0 6px 14px rgba(21,94,239,.18)"
    }
  };
});

const TicketIABar = ({
  mode,
  onAccept,
  onResume
}) => {
  const classes = useStyles();
  const isAi = mode === "ai";

  return (
    <>
      <div
        data-guide="ticket-ai-control"
        className={clsx(
          classes.root,
          isAi ? classes.aiMode : classes.humanMode
        )}
      >
        <div className={classes.content}>
          <div
            className={clsx(
              classes.icon,
              isAi ? classes.aiIcon : classes.humanIcon
            )}
          >
            {isAi ? "🤖" : "⚡"}
          </div>

          <div className={classes.textWrap}>
            <span className={classes.title}>
              {isAi ? "IA em atendimento" : "IA pausada"}
            </span>

            <span className={classes.detail}>
              {isAi
                ? "A Korax está respondendo este cliente."
                : "Atendimento humano em andamento."}
            </span>
          </div>
        </div>

        {isAi ? (
          <button
            type="button"
            className={clsx(
              classes.action,
              classes.acceptAction
            )}
            onClick={onAccept}
          >
            Assumir conversa
          </button>
        ) : (
          <button
            type="button"
            className={clsx(
              classes.action,
              classes.resumeAction
            )}
            onClick={onResume}
          >
            🤖 Retomar IA
          </button>
        )}
      </div>

      <div className={classes.mobileSpacer} />
    </>
  );
};

export default TicketIABar;
